import React, { useId } from 'react';

/**
 * Polished macOS Big-Sur-style app icons — all original inline SVG, no
 * third-party glyph copied (IP-clean by construction). Each icon is a 28×28
 * squircle with:
 *   - a vertical gradient (lighter top → richer bottom),
 *   - a glossy specular highlight clipped to the squircle's top half,
 *   - a subtle 1px inner border for depth,
 *   - a soft outer drop-shadow (via CSS `filter`),
 *   - a centered white (or two-tone) glyph drawn over the squircle.
 *
 * To add an icon, append an entry to APP_ICON_DEFS with:
 *   - top:   the lighter hue (gradient top stop)
 *   - bot:   the deeper hue  (gradient bottom stop + any glyph accents)
 *   - glyph: JSX nodes drawn inside the 28×28 viewBox after the squircle
 *
 * Usage:
 *   <AppIcon name="dashboard" size={22} />
 */

const APP_ICON_DEFS = {
  // ── Dashboard: 2×2 grid of squares ───────────────────────────────────────
  dashboard: {
    top: '#60a5fa', bot: '#2563eb',
    glyph: (
      <g fill="#ffffff">
        <rect x="6"  y="6"  width="7" height="7" rx="1.5" />
        <rect x="15" y="6"  width="7" height="7" rx="1.5" />
        <rect x="6"  y="15" width="7" height="7" rx="1.5" />
        <rect x="15" y="15" width="7" height="7" rx="1.5" />
      </g>
    ),
  },

  // ── OAuth Users: head + shoulders silhouette ─────────────────────────────
  oauthUsers: {
    top: '#a5b4fc', bot: '#6366f1',
    glyph: (
      <g fill="#ffffff">
        <circle cx="14" cy="10.5" r="3.4" />
        <path d="M 6.5 22.5 C 6.5 18.4 9.8 15.5 14 15.5 S 21.5 18.4 21.5 22.5 Z" />
      </g>
    ),
  },

  // ── OAuth Clients: a key (ring + shaft + 2 teeth) ────────────────────────
  oauthClients: {
    top: '#fbbf24', bot: '#d97706',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="11.5" cy="11" r="3.6" />
        <line x1="14"   y1="13.6" x2="21" y2="20.6" />
        <line x1="18"   y1="18.6" x2="20" y2="16.6" />
        <line x1="15.5" y1="16.1" x2="17.5" y2="14.1" />
      </g>
    ),
  },

  // ── Share Token: two interlocking chain links ────────────────────────────
  shareToken: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 11 10 L 9 12 a 4 4 0 0 0 0 5.6 a 4 4 0 0 0 5.6 0 L 16 16" />
        <path d="M 17 18 L 19 16 a 4 4 0 0 0 0 -5.6 a 4 4 0 0 0 -5.6 0 L 12 12" />
      </g>
    ),
  },

  // ── Batch Upload: tray with up-arrow ─────────────────────────────────────
  batchUpload: {
    top: '#c4b5fd', bot: '#7c3aed',
    glyph: (
      <g fill="#ffffff">
        <path d="M 14 6 L 20 12 H 17 V 18 H 11 V 12 H 8 Z" />
        <rect x="6" y="20" width="16" height="2.2" rx="1" />
      </g>
    ),
  },

  // ── Chat: speech bubble with bottom-left tail ────────────────────────────
  chat: {
    top: '#6ee7b7', bot: '#10b981',
    glyph: (
      <path
        d="M 6 7 H 22 C 23.1 7 24 7.9 24 9 V 18 C 24 19.1 23.1 20 22 20 H 14 L 10 23 V 20 H 6 C 4.9 20 4 19.1 4 18 V 9 C 4 7.9 4.9 7 6 7 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Admin: shield + checkmark inside ─────────────────────────────────────
  admin: {
    top: '#fca5a5', bot: '#dc2626',
    glyph: (
      <g>
        <path
          d="M 14 4 L 22 7 V 13 C 22 18 18 22 14 24 C 10 22 6 18 6 13 V 7 Z"
          fill="#ffffff"
        />
        <polyline
          points="10,13 13,16 18,11"
          fill="none" stroke="#dc2626" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Tools: wrench at 45° ─────────────────────────────────────────────────
  tools: {
    top: '#94a3b8', bot: '#475569',
    glyph: (
      <g fill="#ffffff">
        <path d="M 20 8 a 4 4 0 0 1 -5 5 L 8 20 L 5 23 L 4 22 L 7 19 L 14 12 a 4 4 0 0 1 5 -5 Z" />
        <circle cx="17" cy="9" r="1.4" fill="#475569" />
      </g>
    ),
  },

  // ── Folder: classic tab + body ───────────────────────────────────────────
  folder: {
    top: '#fde047', bot: '#ca8a04',
    glyph: (
      <path
        d="M 4 9 H 11 L 13 7 H 22 C 23.1 7 24 7.9 24 9 V 19 C 24 20.1 23.1 21 22 21 H 6 C 4.9 21 4 20.1 4 19 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Pipeline: three Kanban columns, the middle one taller ────────────────
  pipeline: {
    top: '#93c5fd', bot: '#2563eb',
    glyph: (
      <g fill="#ffffff">
        <rect x="5" y="9" width="5" height="12" rx="1.2" />
        <rect x="11.5" y="6" width="5" height="15" rx="1.2" />
        <rect x="18" y="11.5" width="5" height="9.5" rx="1.2" />
      </g>
    ),
  },

  // ── Opportunities: a rising trend line with a target coin ────────────────
  opportunities: {
    top: '#86efac', bot: '#16a34a',
    glyph: (
      <g>
        <path
          d="M 5 20 L 11 14 L 15 17.5 L 23 9"
          fill="none"
          stroke="#ffffff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M 18 8.4 H 23.6 V 14" fill="none" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="11" cy="14" r="1.9" fill="#16a34a" stroke="#ffffff" strokeWidth="1.4" />
      </g>
    ),
  },

  // ── Leads: a funnel — wide intake narrowing to a qualified drip ──────────
  leads: {
    top: '#fdba74', bot: '#ea580c',
    glyph: (
      <g>
        <path
          d="M 5 6.5 H 23 C 23.6 6.5 23.9 7.2 23.5 7.6 L 16.4 15.2 V 21.6 C 16.4 22 16.2 22.4 15.8 22.6 L 12.9 24.2 C 12.3 24.5 11.6 24.1 11.6 23.4 V 15.2 L 4.5 7.6 C 4.1 7.2 4.4 6.5 5 6.5 Z"
          fill="#ffffff"
        />
        <rect x="8.6" y="9.4" width="10.8" height="1.6" rx="0.8" fill="#ea580c" />
      </g>
    ),
  },

  // ── Sales Teams: three busts, the centre one forward ─────────────────────
  salesTeams: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g fill="#ffffff">
        <circle cx="8" cy="11.4" r="2.3" />
        <path d="M 3.6 19.6 C 3.6 16.9 5.6 15.2 8 15.2 S 12.4 16.9 12.4 19.6 Z" />
        <circle cx="20" cy="11.4" r="2.3" />
        <path d="M 15.6 19.6 C 15.6 16.9 17.6 15.2 20 15.2 S 24.4 16.9 24.4 19.6 Z" />
        <circle cx="14" cy="9.6" r="3" />
        <path d="M 8.6 21.8 C 8.6 18.4 11.1 16.2 14 16.2 S 19.4 18.4 19.4 21.8 Z" />
      </g>
    ),
  },

  // ── Assignment Rules: one inbound path branching to two targets ──────────
  assignmentRules: {
    top: '#c4b5fd', bot: '#7c3aed',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 5 14 H 11 C 12.7 14 13.6 12.6 14.4 11.2 C 15.2 9.8 16.1 8.4 17.8 8.4 H 21" />
        <path d="M 11 14 C 12.7 14 13.6 15.4 14.4 16.8 C 15.2 18.2 16.1 19.6 17.8 19.6 H 21" />
        <path d="M 18.6 6 L 21.4 8.4 L 18.6 10.8" />
        <path d="M 18.6 17.2 L 21.4 19.6 L 18.6 22" />
      </g>
    ),
  },

  // ── Live Chat: a speech bubble with a headset arc — a conversation somebody
  //    is being paid to answer, distinct from the plain chat bubble ──────────
  livechat: {
    top: '#67e8f9', bot: '#0891b2',
    glyph: (
      <g>
        <path
          d="M 6 7 H 22 C 23.1 7 24 7.9 24 9 V 17.4 C 24 18.5 23.1 19.4 22 19.4 H 14.6 L 9.8 23 V 19.4 H 6 C 4.9 19.4 4 18.5 4 17.4 V 9 C 4 7.9 4.9 7 6 7 Z"
          fill="#ffffff"
        />
        <path
          d="M 9.6 14.2 V 12.4 C 9.6 10.1 11.5 8.2 14 8.2 C 16.5 8.2 18.4 10.1 18.4 12.4 V 14.2"
          fill="none"
          stroke="#0891b2"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect x="8.2" y="13.4" width="2.8" height="4" rx="1.4" fill="#0891b2" />
        <rect x="17" y="13.4" width="2.8" height="4" rx="1.4" fill="#0891b2" />
      </g>
    ),
  },

  // ── My Day: a calendar sheet with today ticked off ───────────────────────
  myDay: {
    top: '#fcd34d', bot: '#f59e0b',
    glyph: (
      <g>
        <rect x="4.4" y="6.4" width="19.2" height="17.2" rx="2.4" fill="#ffffff" />
        <rect x="4.4" y="6.4" width="19.2" height="4.6" rx="2.4" fill="#f59e0b" />
        <rect x="8" y="3.6" width="2.2" height="4.8" rx="1.1" fill="#ffffff" />
        <rect x="17.8" y="3.6" width="2.2" height="4.8" rx="1.1" fill="#ffffff" />
        <path
          d="M 9.2 16.6 L 12.4 19.6 L 18.8 13.6"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Activities: a checklist — two done, one still open ───────────────────
  activities: {
    top: '#a5b4fc', bot: '#4f46e5',
    glyph: (
      <g>
        <g fill="none" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
          <path d="M 4.6 9.4 L 6.6 11.4 L 10.2 7.4" />
          <path d="M 4.6 16.4 L 6.6 18.4 L 10.2 14.4" />
        </g>
        <circle cx="7.2" cy="22.4" r="2.1" fill="none" stroke="#ffffff" strokeWidth="2.1" />
        <g fill="#ffffff">
          <rect x="13" y="8.2" width="10.4" height="2.2" rx="1.1" />
          <rect x="13" y="15.2" width="10.4" height="2.2" rx="1.1" />
          <rect x="13" y="21.4" width="7.2" height="2.2" rx="1.1" />
        </g>
      </g>
    ),
  },

  // ── Organizations (Accounts): office block — body, roof band, window grid ─
  organizations: {
    top: '#7dd3fc', bot: '#0284c7',
    glyph: (
      <g fill="#ffffff">
        <path d="M 7 8 C 7 7.4 7.4 7 8 7 H 17 C 17.6 7 18 7.4 18 8 V 23 H 7 Z" />
        <path d="M 19 12 H 23 C 23.6 12 24 12.4 24 13 V 23 H 19 Z" />
        <g fill="#0284c7">
          <rect x="9" y="10" width="2.4" height="2.4" rx="0.5" />
          <rect x="13.6" y="10" width="2.4" height="2.4" rx="0.5" />
          <rect x="9" y="14.4" width="2.4" height="2.4" rx="0.5" />
          <rect x="13.6" y="14.4" width="2.4" height="2.4" rx="0.5" />
          <rect x="20.4" y="15" width="2.2" height="2.2" rx="0.5" />
          <rect x="11" y="19" width="3" height="4" rx="0.6" />
        </g>
      </g>
    ),
  },

  // ── Contacts: address card — person bust left, detail lines right ────────
  contacts: {
    top: '#6ee7b7', bot: '#059669',
    glyph: (
      <g>
        <rect x="4" y="7.5" width="20" height="13" rx="2.2" fill="#ffffff" />
        <g fill="#059669">
          <circle cx="10.5" cy="12.2" r="2.1" />
          <path d="M 6.8 18.2 C 6.8 15.9 8.5 14.4 10.5 14.4 S 14.2 15.9 14.2 18.2 Z" />
          <rect x="16" y="11.2" width="5.6" height="1.5" rx="0.75" />
          <rect x="16" y="14.2" width="5.6" height="1.5" rx="0.75" />
          <rect x="16" y="17.2" width="3.6" height="1.5" rx="0.75" />
        </g>
      </g>
    ),
  },

  // ── Tags: label with punched eyelet ──────────────────────────────────────
  tags: {
    top: '#f0abfc', bot: '#a21caf',
    glyph: (
      <g>
        <path
          d="M 13.6 5 H 22 C 23.1 5 24 5.9 24 7 V 15.4 C 24 15.9 23.8 16.4 23.4 16.8 L 15.8 24.4 C 15 25.2 13.8 25.2 13 24.4 L 4.6 16 C 3.8 15.2 3.8 14 4.6 13.2 L 12.2 5.6 C 12.6 5.2 13.1 5 13.6 5 Z"
          fill="#ffffff"
        />
        <circle cx="19.4" cy="9.6" r="1.9" fill="#a21caf" />
      </g>
    ),
  },

  // ── Audit Logs: document with horizontal lines ───────────────────────────
  auditLogs: {
    top: '#d8b4fe', bot: '#9333ea',
    glyph: (
      <g>
        <path
          d="M 8 4 H 17 L 21 8 V 24 C 21 24.55 20.55 25 20 25 H 8 C 7.45 25 7 24.55 7 24 V 5 C 7 4.45 7.45 4 8 4 Z"
          fill="#ffffff"
        />
        <g stroke="#9333ea" strokeWidth="1.4" strokeLinecap="round">
          <line x1="10" y1="12" x2="18" y2="12" />
          <line x1="10" y1="15" x2="18" y2="15" />
          <line x1="10" y1="18" x2="15" y2="18" />
        </g>
      </g>
    ),
  },

  // ── Outbound HTTP: two arrows (one up-right, one down-left) ──────────────
  outboundHttp: {
    top: '#7dd3fc', bot: '#0ea5e9',
    glyph: (
      <g fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6,11 22,11" />
        <polyline points="18,7 22,11 18,15" />
        <polyline points="22,18 6,18" />
        <polyline points="10,22 6,18 10,14" />
      </g>
    ),
  },

  // ── Field History: clock face with hands ─────────────────────────────────
  fieldHistory: {
    top: '#fdba74', bot: '#ea580c',
    glyph: (
      <g>
        <circle cx="14" cy="14" r="8" fill="#ffffff" />
        <g stroke="#ea580c" strokeWidth="1.8" strokeLinecap="round">
          <line x1="14" y1="14" x2="14" y2="9" />
          <line x1="14" y1="14" x2="18" y2="14" />
        </g>
      </g>
    ),
  },

  // ── Login Attempts: arrow entering a door ────────────────────────────────
  loginAttempts: {
    top: '#fda4af', bot: '#e11d48',
    glyph: (
      <g fill="#ffffff">
        <path d="M 13 5 H 22 C 23 5 24 6 24 7 V 21 C 24 22 23 23 22 23 H 13 V 20 H 21 V 8 H 13 Z" />
        <path
          d="M 5 14 L 16 14 M 12 10 L 16 14 L 12 18"
          fill="none" stroke="#ffffff" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Chat Messages: bubble with three dots inside ─────────────────────────
  chatMessages: {
    top: '#d9f99d', bot: '#65a30d',
    glyph: (
      <g>
        <path
          d="M 6 7 H 22 C 23.1 7 24 7.9 24 9 V 18 C 24 19.1 23.1 20 22 20 H 14 L 10 23 V 20 H 6 C 4.9 20 4 19.1 4 18 V 9 C 4 7.9 4.9 7 6 7 Z"
          fill="#ffffff"
        />
        <g fill="#65a30d">
          <circle cx="10" cy="13.5" r="1.2" />
          <circle cx="14" cy="13.5" r="1.2" />
          <circle cx="18" cy="13.5" r="1.2" />
        </g>
      </g>
    ),
  },

  // ── Webhook Deliveries: paper airplane ───────────────────────────────────
  webhookDeliveries: {
    top: '#67e8f9', bot: '#0891b2',
    glyph: (
      <g>
        <path d="M 23 5 L 5 12 L 12 14 L 14 22 Z" fill="#ffffff" />
        <path d="M 12 14 L 23 5" stroke="#0891b2" strokeWidth="1.4" strokeLinecap="round" />
      </g>
    ),
  },

  // ── Failed Jobs: warning triangle with ! ─────────────────────────────────
  failedJobs: {
    top: '#fcd34d', bot: '#d97706',
    glyph: (
      <g>
        <path
          d="M 14 4.5 L 24 22 H 4 Z"
          fill="#ffffff"
        />
        <g fill="#d97706">
          <rect x="13.2" y="11" width="1.6" height="6" rx="0.8" />
          <circle cx="14" cy="19.5" r="1.1" />
        </g>
      </g>
    ),
  },

  // ── Notification: bell with clapper (warm amber — "alert/attention") ─────
  notification: {
    top: '#fcd34d', bot: '#f59e0b',
    glyph: (
      <g fill="#ffffff">
        <path d="M 14 5 a 5.6 5.6 0 0 0 -5.6 5.6 v 3.4 c 0 1.4 -0.5 2.7 -1.4 3.8 L 5.8 19.2 h 16.4 l -1.2 -1.4 c -0.9 -1.1 -1.4 -2.4 -1.4 -3.8 v -3.4 A 5.6 5.6 0 0 0 14 5 Z" />
        <path d="M 11.6 21 a 2.4 2.4 0 0 0 4.8 0 Z" />
      </g>
    ),
  },

  // ── Theme Sun: filled disc + 8 rays (warm gold — "light mode") ───────────
  themeSun: {
    top: '#fde047', bot: '#f59e0b',
    glyph: (
      <g>
        <circle cx="14" cy="14" r="4.6" fill="#ffffff" />
        <g
          fill="none" stroke="#ffffff" strokeWidth="2"
          strokeLinecap="round"
        >
          <line x1="14"   y1="4.5"  x2="14"   y2="7" />
          <line x1="14"   y1="21"   x2="14"   y2="23.5" />
          <line x1="4.5"  y1="14"   x2="7"    y2="14" />
          <line x1="21"   y1="14"   x2="23.5" y2="14" />
          <line x1="7.2"  y1="7.2"  x2="9"    y2="9" />
          <line x1="19"   y1="19"   x2="20.8" y2="20.8" />
          <line x1="7.2"  y1="20.8" x2="9"    y2="19" />
          <line x1="19"   y1="9"    x2="20.8" y2="7.2" />
        </g>
      </g>
    ),
  },

  // ── Theme Moon: crescent (cool indigo — "dark mode") ─────────────────────
  themeMoon: {
    top: '#a5b4fc', bot: '#4338ca',
    glyph: (
      <path
        d="M 21 16 a 8 8 0 1 1 -10 -10 a 6.4 6.4 0 0 0 10 10 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── Voice Call: classic phone handset, tilted, with a soft inner cradle ──
  // Two-tone — white handset glyph on an emerald gradient (matches the chat
  // family so voice/video read as "from the chat module"). The inner cradle
  // line is the bottom gradient hue, picking up the chat green for cohesion.
  voiceCall: {
    top: '#6ee7b7', bot: '#059669',
    glyph: (
      <g>
        <path
          d="M 8.4 5.4 L 11.4 6.6 C 12.2 6.9 12.6 7.8 12.3 8.6 L 11.3 11.3 C 11.1 11.9 11.3 12.6 11.8 13 L 15 16.2 C 15.4 16.6 16.1 16.8 16.7 16.6 L 19.4 15.6 C 20.2 15.3 21.1 15.7 21.4 16.5 L 22.6 19.5 C 22.9 20.4 22.5 21.3 21.7 21.7 C 14.5 25.5 5.5 16.5 9.3 9.3 C 9.7 8.5 10.6 8.1 11.5 8.4"
          fill="#ffffff"
        />
        <path
          d="M 13 17 L 17 21"
          stroke="#059669" strokeWidth="0.9" strokeLinecap="round"
          fill="none" opacity="0.45"
        />
      </g>
    ),
  },

  // ── Video Call: camcorder body + lens with a subtle play-triangle hint ───
  // Two-tone — white body on a sky-blue gradient (distinct from voice but
  // still a "communication" hue). The lens accent is the deeper bottom hue
  // so the lens reads as a real circular aperture, not a flat dot.
  videoCall: {
    top: '#7dd3fc', bot: '#0284c7',
    glyph: (
      <g>
        {/* Camera body */}
        <rect x="5" y="9" width="14" height="10" rx="2" fill="#ffffff" />
        {/* Lens nozzle on the right — the play-triangle hint */}
        <path d="M 19 12 L 24 9 V 19 L 19 16 Z" fill="#ffffff" />
        {/* Inset lens (camera aperture) */}
        <circle cx="11" cy="14" r="2.4" fill="#0284c7" />
        <circle cx="11" cy="14" r="1.2" fill="#ffffff" opacity="0.75" />
        {/* Tiny REC dot — top-left of body, signals "records by default" */}
        <circle cx="7.4" cy="11.4" r="0.7" fill="#ef4444" />
      </g>
    ),
  },

  // ── Google: a hand-authored multi-colour "G" arc on a clean white tile.
  // Trademark-conscious — geometrically inspired by Google's brand colour
  // palette without copying the official glyph proportions. The white tile
  // base keeps it readable on either light or dark Aqua chrome.
  googleLogo: {
    top: '#ffffff', bot: '#f1f3f4',
    glyph: (
      <g fill="none" strokeWidth="2.8" strokeLinecap="round">
        {/* Outer "G" arc, four quadrants in the recognisable Google palette */}
        <path d="M 21 14 a 7 7 0 1 1 -2.05 -4.95"  stroke="#4285F4" />
        <path d="M 21 14 a 7 7 0 0 1 -3.5 6.06"     stroke="#34A853" />
        <path d="M 17.5 20.06 a 7 7 0 0 1 -10.5 -1" stroke="#FBBC05" />
        <path d="M 7    19.06 a 7 7 0 0 1 1.05 -10" stroke="#EA4335" />
        {/* Crossbar — the right-side stub that signals "G" rather than "C" */}
        <path d="M 14.5 14 H 21" stroke="#4285F4" />
      </g>
    ),
  },

  // ── Facebook: lowercase "f" geometric letterform on Facebook-blue tile.
  // Hand-drawn glyph — vertical stem + crossbar + top arc, not a trace of
  // the official mark.
  facebookLogo: {
    top: '#3b82f6', bot: '#1d4ed8',
    glyph: (
      <g fill="#ffffff">
        {/* Vertical stem of the "f" */}
        <rect x="14.5" y="9.5" width="3" height="13.5" rx="0.4" />
        {/* Top arc — the hook that turns the stem into an "f" */}
        <path d="M 17.5 5 a 3.5 3.5 0 0 0 -3.5 3.5 V 11 H 12 V 14 H 14 V 9 a 1.5 1.5 0 0 1 1.5 -1.5 H 17.5 Z" />
        {/* Crossbar */}
        <rect x="12" y="13.4" width="6.5" height="2.6" rx="0.4" />
      </g>
    ),
  },

  // ── Manage (group participants): two head silhouettes + a plus badge ─────
  // v1.75 — sits next to voice/video in the group chat header. Indigo
  // gradient distinct from the user-management amber so it reads as
  // "manage THIS group" rather than "manage all users". The `+` badge
  // in the top-right corner cues the "add participants" action that the
  // panel surfaces.
  manage: {
    top: '#a78bfa', bot: '#7c3aed',
    glyph: (
      <g>
        {/* Two silhouettes — back person shifted up-right behind front */}
        <g fill="#ffffff">
          {/* Back head */}
          <circle cx="18" cy="9.5" r="2.6" />
          <path d="M 13.5 17 C 13.5 14.5 15.5 12.7 18 12.7 C 20.5 12.7 22.5 14.5 22.5 17 Z" />
          {/* Front head */}
          <circle cx="11.5" cy="11.5" r="3.2" />
          <path d="M 5.5 21.5 C 5.5 17.9 8.2 15.2 11.5 15.2 C 14.8 15.2 17.5 17.9 17.5 21.5 Z" />
        </g>
        {/* Plus badge — top-right corner, white plus on the deeper hue
            so it reads as a button-like affordance distinct from the
            silhouettes. */}
        <circle cx="22" cy="6.5" r="3.2" fill="#7c3aed" stroke="#ffffff" strokeWidth="1.1" />
        <path d="M 22 4.7 V 8.3 M 20.2 6.5 H 23.8" stroke="#ffffff" strokeWidth="1.1" strokeLinecap="round" />
      </g>
    ),
  },

  // ── View (details panel): an info-card sheet with three list lines ───────
  // v1.75 — sits next to voice/video. Teal gradient pairs with the call
  // family (emerald voice + sky-blue video) so the trio reads as one
  // chat-header toolset. The sheet has a folded top-right corner (the
  // classic "page" affordance) and three indented lines suggesting the
  // Media / Files / Links / Pinned sections inside the panel.
  view: {
    top: '#5eead4', bot: '#0d9488',
    glyph: (
      <g>
        {/* Page body with a small folded corner */}
        <path
          d="M 7 5 H 18 L 21 8 V 23 H 7 Z"
          fill="#ffffff"
        />
        {/* Folded corner — the deeper hue so the fold reads as the
            page-corner shadow rather than a separate shape. */}
        <path d="M 18 5 V 8 H 21 Z" fill="#0d9488" opacity="0.55" />
        {/* Three list lines — slightly different widths so the sheet
            reads as content, not a blank page. */}
        <g fill="#0d9488">
          <rect x="9.5"  y="11"   width="9"  height="1.6" rx="0.4" />
          <rect x="9.5"  y="14.2" width="7"  height="1.6" rx="0.4" />
          <rect x="9.5"  y="17.4" width="8.5" height="1.6" rx="0.4" />
        </g>
      </g>
    ),
  },

  // ═══════════════════════════════════════════════════════════════════════
  // RMC2 domain icons. Same construction as everything above — original
  // geometry drawn in the 28×28 squircle, no third-party glyph copied.
  // Hues follow the nav grouping so the rail reads as sections at a glance:
  // navy/indigo for member records, gold for money, teal for support.
  // ═══════════════════════════════════════════════════════════════════════

  // ── Members: two overlapping head + shoulders ────────────────────────────
  users: {
    top: '#818cf8', bot: '#4338ca',
    glyph: (
      <g>
        {/* Back figure, dimmed so the pair reads as depth not clutter */}
        <g fill="#ffffff" opacity="0.55">
          <circle cx="18.8" cy="10.6" r="2.7" />
          <path d="M 13.6 21.5 C 13.6 18.2 15.9 15.9 18.8 15.9 S 24 18.2 24 21.5 Z" />
        </g>
        <g fill="#ffffff">
          <circle cx="11.6" cy="10.2" r="3.4" />
          <path d="M 4.8 22.4 C 4.8 18.4 7.8 15.6 11.6 15.6 S 18.4 18.4 18.4 22.4 Z" />
        </g>
      </g>
    ),
  },

  // ── Branches: office block, windows lit ──────────────────────────────────
  building: {
    top: '#7dd3fc', bot: '#0369a1',
    glyph: (
      <g>
        <path d="M 7 6.5 H 17 A 1.2 1.2 0 0 1 18.2 7.7 V 23 H 5.8 V 7.7 A 1.2 1.2 0 0 1 7 6.5 Z" fill="#ffffff" />
        {/* Annex — reads as a multi-branch office rather than a single tower */}
        <path d="M 19.4 12.5 H 22.4 A 1 1 0 0 1 23.4 13.5 V 23 H 19.4 Z" fill="#ffffff" opacity="0.62" />
        <g fill="#0369a1">
          <rect x="8"    y="9.4"  width="2.6" height="2.6" rx="0.5" />
          <rect x="13.4" y="9.4"  width="2.6" height="2.6" rx="0.5" />
          <rect x="8"    y="14"   width="2.6" height="2.6" rx="0.5" />
          <rect x="13.4" y="14"   width="2.6" height="2.6" rx="0.5" />
          {/* Doorway */}
          <path d="M 10.6 23 V 19.2 A 1.4 1.4 0 0 1 13.4 19.2 V 23 Z" />
        </g>
      </g>
    ),
  },

  // ── Billing: receipt with a torn bottom edge ─────────────────────────────
  receipt: {
    top: '#fcd34d', bot: '#b45309',
    glyph: (
      <g>
        {/* Torn lower edge is what makes this read as a receipt, not a page */}
        <path
          d="M 7 5.5 H 21 V 22.5 L 19 21 L 17 22.5 L 15 21 L 13 22.5 L 11 21 L 9 22.5 L 7 21 Z"
          fill="#ffffff"
        />
        <g fill="#b45309">
          <rect x="9.5" y="9"    width="9"   height="1.6" rx="0.5" />
          <rect x="9.5" y="12.4" width="6.5" height="1.6" rx="0.5" />
          <rect x="9.5" y="15.8" width="8"   height="1.6" rx="0.5" />
        </g>
      </g>
    ),
  },

  // ── Claims: ring buoy — the mutual-aid "we've got you" mark ──────────────
  lifebuoy: {
    top: '#fda4af', bot: '#be123c',
    glyph: (
      <g>
        <circle cx="14" cy="14" r="8.4" fill="#ffffff" />
        <circle cx="14" cy="14" r="3.5" fill="#be123c" />
        {/* Four grab-bands at the diagonals */}
        <g stroke="#be123c" strokeWidth="2.2" strokeLinecap="round">
          <line x1="8.1"  y1="8.1"  x2="11.2" y2="11.2" />
          <line x1="19.9" y1="8.1"  x2="16.8" y2="11.2" />
          <line x1="8.1"  y1="19.9" x2="11.2" y2="16.8" />
          <line x1="19.9" y1="19.9" x2="16.8" y2="16.8" />
        </g>
      </g>
    ),
  },

  // ── Network: one referrer, three downline nodes ──────────────────────────
  network: {
    top: '#67e8f9', bot: '#0e7490',
    glyph: (
      <g>
        <g stroke="#ffffff" strokeWidth="1.7" strokeLinecap="round" fill="none">
          <path d="M 14 10.4 V 14.2" />
          <path d="M 7.5 18.4 V 16.2 H 20.5 V 18.4" />
        </g>
        <g fill="#ffffff">
          <circle cx="14"   cy="7.6"  r="3.1" />
          <circle cx="7.5"  cy="20.6" r="2.6" />
          <circle cx="14"   cy="20.6" r="2.6" />
          <circle cx="20.5" cy="20.6" r="2.6" />
        </g>
      </g>
    ),
  },

  // ── Registration QR: finder squares + a scatter of modules ───────────────
  qrCode: {
    top: '#94a3b8', bot: '#334155',
    glyph: (
      <g fill="#ffffff">
        {/* Three finder squares — the part of a QR everyone recognises */}
        <path d="M 5.5 5.5 h 7 v 7 h -7 Z M 7.4 7.4 v 3.2 h 3.2 v -3.2 Z" fillRule="evenodd" />
        <path d="M 15.5 5.5 h 7 v 7 h -7 Z M 17.4 7.4 v 3.2 h 3.2 v -3.2 Z" fillRule="evenodd" />
        <path d="M 5.5 15.5 h 7 v 7 h -7 Z M 7.4 17.4 v 3.2 h 3.2 v -3.2 Z" fillRule="evenodd" />
        <g opacity="0.85">
          <rect x="15.5" y="15.5" width="3"   height="3"   rx="0.6" />
          <rect x="19.9" y="15.5" width="2.6" height="2.6" rx="0.6" />
          <rect x="15.5" y="19.9" width="2.6" height="2.6" rx="0.6" />
          <rect x="19.9" y="19.9" width="2.6" height="2.6" rx="0.6" />
        </g>
      </g>
    ),
  },

  // ── Register member: a figure with a plus ────────────────────────────────
  userPlus: {
    top: '#6ee7b7', bot: '#047857',
    glyph: (
      <g fill="#ffffff">
        <circle cx="12" cy="10.4" r="3.6" />
        <path d="M 5.4 22 C 5.4 18.2 8.4 15.6 12 15.6 C 14 15.6 15.8 16.4 17 17.7 V 22 Z" />
        <g opacity="0.95">
          <rect x="18.4" y="8.2"  width="1.9" height="7.4" rx="0.95" />
          <rect x="15.6" y="11"   width="7.4" height="1.9" rx="0.95" />
        </g>
      </g>
    ),
  },

  // ── Payouts: a wallet with a card slot ───────────────────────────────────
  wallet: {
    top: '#fcd34d', bot: '#b45309',
    glyph: (
      <g>
        <rect x="4.5" y="8" width="19" height="13" rx="2.6" fill="#ffffff" />
        <path d="M 4.5 11.4 V 10.6 C 4.5 9.2 5.6 8 7.1 8 H 18 V 11.4 Z" fill="#ffffff" opacity="0.7" />
        <rect x="15.2" y="12.4" width="8.3" height="4.6" rx="1.6" fill="#b45309" />
        <circle cx="18.4" cy="14.7" r="1.35" fill="#ffffff" />
      </g>
    ),
  },

  // ── Renewal: two arrows chasing each other round a cycle ─────────────────
  renewal: {
    top: '#fda4af', bot: '#9f1239',
    glyph: (
      <g stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round">
        <path d="M 21 14 A 7 7 0 0 1 8.6 18.4" />
        <path d="M 7 14 A 7 7 0 0 1 19.4 9.6" />
        <path d="M 19.4 6.1 V 9.8 H 15.7" strokeLinejoin="round" />
        <path d="M 8.6 21.9 V 18.2 H 12.3" strokeLinejoin="round" />
      </g>
    ),
  },

  // ── Activity: a heartbeat trace ──────────────────────────────────────────
  activity: {
    top: '#a5b4fc', bot: '#4338ca',
    glyph: (
      <g stroke="#ffffff" strokeWidth="2.1" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 4.8 14 H 9 L 11.4 8.4 L 16.2 19.6 L 18.6 14 H 23.2" />
      </g>
    ),
  },

  // ── Reports: ascending bar chart on an axis ──────────────────────────────
  chart: {
    top: '#86efac', bot: '#15803d',
    glyph: (
      <g>
        <g fill="#ffffff">
          <rect x="7"    y="15.5" width="3.4" height="6.5" rx="1" />
          <rect x="12.3" y="11"   width="3.4" height="11"  rx="1" />
          <rect x="17.6" y="7.5"  width="3.4" height="14.5" rx="1" />
        </g>
        <rect x="5.5" y="22.4" width="17" height="1.7" rx="0.85" fill="#ffffff" opacity="0.62" />
      </g>
    ),
  },

  // ── My Membership: the member ID card ────────────────────────────────────
  idCard: {
    top: '#e9c46a', bot: '#a16207',
    glyph: (
      <g>
        <rect x="4.5" y="7.5" width="19" height="13.5" rx="2.2" fill="#ffffff" />
        <g fill="#a16207">
          <circle cx="10.4" cy="12.8" r="2.5" />
          <path d="M 6.6 18.6 C 6.6 16.6 8.3 15.2 10.4 15.2 S 14.2 16.6 14.2 18.6 Z" />
          <rect x="16"   y="11.6" width="5.4" height="1.5" rx="0.5" />
          <rect x="16"   y="14.4" width="5.4" height="1.5" rx="0.5" />
          <rect x="16"   y="17.2" width="3.6" height="1.5" rx="0.5" />
        </g>
      </g>
    ),
  },

  // ── Benefits: protective shield ──────────────────────────────────────────
  shield: {
    top: '#93c5fd', bot: '#1d4ed8',
    glyph: (
      <path
        d="M 14 4.8 L 22 8 V 14 C 22 18.8 18.6 22.4 14 23.6 C 9.4 22.4 6 18.8 6 14 V 8 Z"
        fill="#ffffff"
      />
    ),
  },

  // ── System Administrator: shield with a check ────────────────────────────
  shieldCheck: {
    top: '#a7f3d0', bot: '#047857',
    glyph: (
      <g>
        <path
          d="M 14 4.8 L 22 8 V 14 C 22 18.8 18.6 22.4 14 23.6 C 9.4 22.4 6 18.8 6 14 V 8 Z"
          fill="#ffffff"
        />
        <polyline
          points="10.4,14 13.1,16.7 18,11.4"
          fill="none" stroke="#047857" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round"
        />
      </g>
    ),
  },

  // ── Bank Holidays: calendar with hangers ─────────────────────────────────
  calendar: {
    top: '#f9a8d4', bot: '#a21caf',
    glyph: (
      <g>
        <rect x="5" y="7" width="18" height="16" rx="2.2" fill="#ffffff" />
        {/* Header band + the two hangers */}
        <path d="M 5 9.2 A 2.2 2.2 0 0 1 7.2 7 H 20.8 A 2.2 2.2 0 0 1 23 9.2 V 11.6 H 5 Z" fill="#a21caf" />
        <g stroke="#ffffff" strokeWidth="1.9" strokeLinecap="round">
          <line x1="10" y1="4.6" x2="10" y2="8.2" />
          <line x1="18" y1="4.6" x2="18" y2="8.2" />
        </g>
        <g fill="#a21caf">
          <circle cx="9.6"  cy="15.2" r="1.25" />
          <circle cx="14"   cy="15.2" r="1.25" />
          <circle cx="18.4" cy="15.2" r="1.25" />
          <circle cx="9.6"  cy="19.4" r="1.25" />
          <circle cx="14"   cy="19.4" r="1.25" />
        </g>
      </g>
    ),
  },

  // ── Settings: gear (8 teeth around a hub) ────────────────────────────────
  settings: {
    top: '#cbd5e1', bot: '#475569',
    glyph: (
      <g>
        <g fill="#ffffff">
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <rect
              key={deg}
              x="12.9" y="3.6" width="2.2" height="4.6" rx="0.9"
              transform={`rotate(${deg} 14 14)`}
            />
          ))}
        </g>
        <circle cx="14" cy="14" r="6.4" fill="#ffffff" />
        <circle cx="14" cy="14" r="2.7" fill="#475569" />
      </g>
    ),
  },

  // ── Share Tokens / keys: key with two teeth ──────────────────────────────
  key: {
    top: '#fde68a', bot: '#ca8a04',
    glyph: (
      <g>
        <circle cx="10.8" cy="10.8" r="4.9" fill="#ffffff" />
        <circle cx="10.8" cy="10.8" r="1.9" fill="#ca8a04" />
        <g stroke="#ffffff" strokeWidth="2.4" strokeLinecap="round">
          <line x1="13.6" y1="13.6" x2="21.4" y2="21.4" />
          <line x1="18.2" y1="19"   x2="20.2" y2="17" />
          <line x1="15.6" y1="16.4" x2="17.6" y2="14.4" />
        </g>
      </g>
    ),
  },

  // ── Log viewers: bulleted list ───────────────────────────────────────────
  list: {
    top: '#d8b4fe', bot: '#7e22ce',
    glyph: (
      <g fill="#ffffff">
        <circle cx="7.8" cy="9.4"  r="1.7" />
        <circle cx="7.8" cy="14"   r="1.7" />
        <circle cx="7.8" cy="18.6" r="1.7" />
        <rect x="11.6" y="8.4"  width="9.6" height="2.1" rx="1.05" />
        <rect x="11.6" y="13"   width="9.6" height="2.1" rx="1.05" />
        <rect x="11.6" y="17.6" width="6.8" height="2.1" rx="1.05" />
      </g>
    ),
  },
};

export default function AppIcon({ name, size = 22, className }) {
  const def = APP_ICON_DEFS[name];
  const uid = useId();
  if (!def) return null;

  // Image-source override: when the icon definition carries an `imgSrc`,
  // composite the logo INSIDE the polished squircle (via `LogoBadge`) so it
  // reads like every other AppIcon — a brand-tinted frame with the asset as
  // the glyph — instead of a bare PNG. Used by brand-image entries; the project
  // swaps one PNG file (`public/logo192.png`) and every nav surface updates.
  if (def.imgSrc) {
    return (
      <LogoBadge
        src={def.imgSrc}
        alt={def.alt}
        size={size}
        top={def.top}
        bot={def.bot}
        className={className}
      />
    );
  }

  const gradId = `aig-${uid}`;
  const glossId = `aih-${uid}`;
  const clipId = `aic-${uid}`;
  return (
    <svg
      viewBox="0 0 28 28"
      width={size}
      height={size}
      className={className}
      style={{ filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.18))' }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={def.top} />
          <stop offset="1" stopColor={def.bot} />
        </linearGradient>
        <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1"   stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="28" height="28" rx="6.5" ry="6.5" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* Squircle background */}
        <rect width="28" height="28" fill={`url(#${gradId})`} />
        {/* Specular glossy sheen (top half) */}
        <rect width="28" height="14" fill={`url(#${glossId})`} />
        {/* Subtle inner border for depth */}
        <rect
          x="0.5" y="0.5" width="27" height="27" rx="6.1" ry="6.1"
          fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1"
        />
        {def.glyph}
      </g>
    </svg>
  );
}

/**
 * LogoBadge — a brand/logo image composited INSIDE the polished macOS-style
 * squircle (gradient frame + glossy top-half sheen + inner border + soft
 * drop-shadow), with the image inset so the frame shows around it (the same
 * "icon inside a background" look the inline-SVG AppIcons have, but the glyph
 * is the project's logo PNG). Used for sidebar brand entries and
 * the navbar logo so a bare-PNG logo matches the rest of the icon family.
 *
 * `top`/`bot` tint the frame gradient (default = the brand accent; a brand
 * sidebar passes brand pink). `alt` makes it an accessible image; omit for a
 * decorative mark. Swap the underlying PNG (or REACT_APP_LOGO_URL) and every
 * badge updates — Rule 56 default-logo convention.
 */
export function LogoBadge({
  src,
  alt = '',
  size = 22,
  top = '#5eaaff',
  bot = '#2563eb',
  className,
}) {
  const uid = useId();
  const gradId  = `lbg-${uid}`;
  const glossId = `lbh-${uid}`;
  const clipId  = `lbc-${uid}`;
  const imgClip = `lbi-${uid}`;
  return (
    <svg
      viewBox="0 0 28 28"
      width={size}
      height={size}
      className={className}
      style={{ filter: 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.18))' }}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={top} />
          <stop offset="1" stopColor={bot} />
        </linearGradient>
        <linearGradient id={glossId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="0.6" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="1"   stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="28" height="28" rx="6.5" ry="6.5" />
        </clipPath>
        <clipPath id={imgClip}>
          <rect x="3.5" y="3.5" width="21" height="21" rx="5" ry="5" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {/* Brand-tinted squircle background */}
        <rect width="28" height="28" fill={`url(#${gradId})`} />
        {/* The logo, inset so the frame reads as a border around the mark */}
        <image
          href={src}
          x="3.5" y="3.5" width="21" height="21"
          clipPath={`url(#${imgClip})`}
          preserveAspectRatio="xMidYMid slice"
        />
        {/* Specular glossy sheen (top half) — same as the glyph icons */}
        <rect width="28" height="14" fill={`url(#${glossId})`} />
        {/* Subtle inner border for depth */}
        <rect
          x="0.5" y="0.5" width="27" height="27" rx="6.1" ry="6.1"
          fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1"
        />
      </g>
    </svg>
  );
}

export { APP_ICON_DEFS };
